import { ExcalidrawApp } from '@jitsi/excalidraw';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-expect-error
import Filmstrip from '../../../../../modules/UI/videolayout/Filmstrip';
import { translate } from '../../../base/i18n/functions';
import { IconCloseLarge } from '../../../base/icons/svg';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants/functions';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { getVerticalViewMaxWidth } from '../../../filmstrip/functions.web';
import { getToolboxHeight } from '../../../toolbox/functions.web';
import { shouldDisplayTileView } from '../../../video-layout/functions.any';
import { setWhiteboardOpen } from '../../actions.any';
import { WHITEBOARD_UI_OPTIONS } from '../../constants';
import { getCollabDetails, getCollabServerUrl, isWhiteboardOpen } from '../../functions';
/**
 * Space taken by meeting elements like the subject and the watermark.
 */
const HEIGHT_OFFSET = 80;
/**
 * The Whiteboard component.
 *
 * @param {Props} props - The React props passed to this component.
 * @returns {JSX.Element} - The React component.
 */
const Whiteboard = (props) => {
    const dispatch = useDispatch();
    const excalidrawRef = useRef(null);
    const excalidrawAPIRef = useRef(null);
    const collabAPIRef = useRef(null);
    const isModerator = useSelector(isLocalParticipantModerator);
    const isOpen = useSelector(isWhiteboardOpen);
    const isInTileView = useSelector(shouldDisplayTileView);
    const { clientHeight, videoSpaceWidth } = useSelector((state) => state['features/base/responsive-ui']);
    const { visible: filmstripVisible, isResizing: isFilmstripResizing } = useSelector((state) => state['features/filmstrip']);
    const isChatResizing = useSelector((state) => state['features/chat'].isResizing);
    const isResizing = isFilmstripResizing || isChatResizing;
    const filmstripWidth = useSelector(getVerticalViewMaxWidth);
    const collabDetails = useSelector(getCollabDetails);
    const collabServerUrl = useSelector(getCollabServerUrl);
    const { defaultRemoteDisplayName } = useSelector((state) => state['features/base/config']);
    const localParticipantName = useSelector(getLocalParticipant)?.name || defaultRemoteDisplayName || 'Fellow Jitster';
    useEffect(() => {
        if (!collabAPIRef.current) {
            return;
        }
        collabAPIRef.current.setUsername(localParticipantName);
    }, [localParticipantName]);
    /**
    * Computes the width and the height of the component.
    *
    * @returns {IDimensions} - The dimensions of the component.
    */
    const getDimensions = () => {
        let width;
        let height;
        if (interfaceConfig.VERTICAL_FILMSTRIP) {
            if (filmstripVisible) {
                width = videoSpaceWidth - filmstripWidth;
            }
            else {
                width = videoSpaceWidth;
            }
            height = clientHeight - getToolboxHeight();
        }
        else {
            if (filmstripVisible) {
                height = clientHeight - Filmstrip.getFilmstripHeight();
            }
            else {
                height = clientHeight;
            }
            width = videoSpaceWidth;
        }
        return {
            width: `${width}px`,
            height: `${height - HEIGHT_OFFSET}px`
        };
    };
    const getExcalidrawAPI = useCallback(excalidrawAPI => {
        if (excalidrawAPIRef.current) {
            return;
        }
        excalidrawAPIRef.current = excalidrawAPI;
    }, []);
    const getCollabAPI = useCallback(collabAPI => {
        if (collabAPIRef.current) {
            return;
        }
        collabAPIRef.current = collabAPI;
        collabAPIRef.current.setUsername(localParticipantName);
    }, [localParticipantName]);
    const onCloseWhiteboard = useCallback(() => {
        dispatch(setWhiteboardOpen(false));
    }, [dispatch]);
    // Belltroop: Whiteboard is strictly private to moderators. Defensive
    // guard — even if the open state somehow leaks to a non-moderator
    // (e.g., demoted while open), render nothing so the UI is hidden.
    if (!isModerator) {
        return React.createElement(React.Fragment, null);
    }
    // Belltroop: Whiteboard stays visible to the moderator whenever it's
    // open, regardless of pin state. This keeps it on screen when the
    // moderator starts screen sharing — so the whiteboard content is
    // captured in the outgoing share stream and attendees can see it.
    // Previously, starting a screen share auto-pinned the share
    // participant, unpinned the whiteboard, set isVisible=false and
    // display:none, hiding the whiteboard from the moderator's own view.
    return (React.createElement("div", { className: clsx(isResizing && 'disable-pointer', 'whiteboard-container'), style: {
            ...getDimensions(),
            marginTop: `${HEIGHT_OFFSET}px`,
            display: `${isInTileView || !isOpen ? 'none' : 'block'}`
        } },
        isModerator && (React.createElement("div", { className: 'whiteboard-close-button' },
            React.createElement(ClickableIcon, { accessibilityLabel: props.t('whiteboard.close'), icon: IconCloseLarge, onClick: onCloseWhiteboard }))),
        isOpen && (React.createElement("div", { className: 'excalidraw-wrapper' }, /*
              * Excalidraw renders a few lvl 2 headings. This is
              * quite fortunate, because we actually use lvl 1
              * headings to mark the big sections of our app. So make
              * sure to mark the Excalidraw context with a lvl 1
              * heading before showing the whiteboard.
              */
            React.createElement("span", { "aria-level": 1, className: 'sr-only', role: 'heading' }, props.t('whiteboard.accessibilityLabel.heading')),
            React.createElement(ExcalidrawApp, { collabDetails: collabDetails, collabServerUrl: collabServerUrl, excalidraw: {
                    isCollaborating: true,
                    langCode: i18next.language,
                    // @ts-ignore
                    ref: excalidrawRef,
                    theme: 'light',
                    UIOptions: WHITEBOARD_UI_OPTIONS,
                    viewModeEnabled: !isModerator
                }, getCollabAPI: getCollabAPI, getExcalidrawAPI: getExcalidrawAPI })))));
};
export default translate(Whiteboard);
